import { eqStrict } from 'fp-ts/lib/Eq';
import { useEffect, useReducer, useRef } from 'react';

interface DeferredChangeOptions {
  /**
   * Time in milliseconds between the changed state arrives and useDeferredChange triggers
   * rerender to apply the change
   *
   * This seems that it must be determined experimentally for each use, even 5 or 10 ms may be
   * not enough to render the intermediate state always.
   *
   * Defaults to 0 to prioritize state consistency over UX when nothing is specified.
   */
  applyChangeTimeout?: number;
}

const noPrevValue = Symbol('no prev value');

/**
 * Defer complex value change e.g. to show re-rendering indication
 *
 * IMPORTANT: When you need to use this hook, you are probably doing
 * nasty things. Renders should be fast and when they aren't, you should always first
 * consider all options to just make them fast (better UI, load content by smaller chunks etc).
 * Use this hook only in case you really cannot make your renders faster.
 *
 * Normally, this hook passes thru the value passed as first argument as usedValue.
 *
 * But, when the item changes in a way specified by `isChanged` argument:
 * * previous value and `changed: true` is returned in given render,
 * * immediate rerender is scheduled to render again with the updated value.
 *
 * This allows to render some loading indicator when `changed: true` arrives
 * when we expect the next render to possibly last long, so the UI won't seem unresponsive.
 *
 * Note: on first render, `changed: false` and the passed thru value is always returned.
 *
 * @example
 * const TodoList: React.FC<Props> = bareProps => {
 *   const [props, itemsChanged] = useDeferredChange(
 *     bareProps,
 *     // we assume that re-rendering todoItems will take long
 *     (a, b) => a.todoItems !== b.todoItems,
 *   );
 *
 *   return <>
 *     <h1>{props.title}</h1>
 *     {itemsChanged && <LoadingIndicator />}
 *     <TodoListItems items={props.todoItems} />
 *   </>;
 * }
 */
export const useDeferredChange = <T,>(
  value: T,
  isChanged: (prev: T, current: T) => boolean = eqStrict.equals,
  options?: DeferredChangeOptions,
): [
  usedValue: T,
  changed: boolean,
  details: {
    firstRender: boolean;
    /**
     * Returned only when changed is true to be able to obtain details
     * of what exactly has been changed
     */
    prev?: T;
  },
] => {
  const lastRef = useRef<T | typeof noPrevValue>(noPrevValue);
  const [, rerender] = useReducer((c) => c + 1, 0);

  const changed =
    lastRef.current !== noPrevValue && isChanged(lastRef.current, value);

  useEffect(() => {
    lastRef.current = value;
    if (changed) {
      // without the timeout, it sometimes renders the intermediate state (e.g. some spinner) and sometimes not
      setTimeout(() => {
        rerender();
      }, options?.applyChangeTimeout ?? 0);
    }
  });

  return [
    changed ? (lastRef.current as T) : value,
    changed,
    {
      firstRender: lastRef.current === noPrevValue,
      prev: changed ? (lastRef.current as T) : undefined,
    },
  ];
};
